import { usePermissions } from '@feature/authentication/hooks/usePermissions'
import { useTenantBrandingContext } from '@feature/branding/hooks/useTenantBrandingContext'
import { isSettingsPath } from '@feature/settings/settings-navigation'
import { Button } from '@shared/components/ui/button'
import { buildInfo, formatBuildTimestamp } from '@shared/lib/build-info'
import { cn } from '@shared/lib/utils/cn'
import {
  BarChart3,
  CalendarDays,
  ChevronDown,
  FileText,
  Home,
  Mail,
  Settings2,
  X,
} from 'lucide-react'
import { Link, useLocation } from 'react-router'

interface SidebarProps {
  isOpen: boolean
  onClose: () => void
}

interface NavItem {
  label: string
  group: string
  description: string
  path: string
  icon: React.ComponentType<{ className?: string }>
  permission?: 'manager' | 'analytics' | 'admin'
  /** Item exclusivo de quem não é Gestor: o Leitor vê um único lugar para os avisos. */
  readerOnly?: boolean
}

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const location = useLocation()
  const { canViewAnalytics, isManager, isAdmin } = usePermissions()
  const { branding } = useTenantBrandingContext()

  const navItems: NavItem[] = [
    {
      label: 'Início',
      group: 'Visão geral',
      description: 'Seu resumo',
      path: '/dashboard',
      icon: Home,
    },
    {
      label: 'Avisos',
      group: 'Comunicação',
      description: 'O que há de novo para você',
      path: '/board',
      icon: FileText,
      readerOnly: true,
    },
    {
      label: 'Mural de recados',
      group: 'Comunicação',
      description: 'Avisos para você',
      path: '/board',
      icon: FileText,
      permission: 'manager',
    },
    {
      label: 'Comunicados',
      group: 'Comunicação',
      description: 'Publicações',
      path: '/communications',
      icon: FileText,
      permission: 'manager',
    },
    {
      label: 'Calendário',
      group: 'Comunicação',
      description: 'Eventos e alertas',
      path: '/calendar',
      icon: CalendarDays,
    },
    {
      label: 'Painel de leituras',
      group: 'Acompanhamento',
      description: 'Lidos e não lidos',
      path: '/readership',
      icon: BarChart3,
      permission: 'manager',
    },
    {
      label: 'Relatórios',
      group: 'Acompanhamento',
      description: 'Métricas',
      path: '/analytics',
      icon: BarChart3,
      permission: 'analytics',
    },
    {
      label: 'Envios do WhatsApp',
      group: 'Acompanhamento',
      description: 'Entregas e falhas',
      path: '/whatsapp',
      icon: Mail,
      permission: 'manager',
    },
    {
      label: 'Modelos de comunicado',
      group: 'Comunicação',
      description: 'Conteúdo reutilizável',
      path: '/communication-templates',
      icon: Mail,
      permission: 'manager',
    },
    {
      label: 'Operação',
      group: 'Acompanhamento',
      description: 'Processamento e pendências',
      path: '/operations',
      icon: Settings2,
      permission: 'manager',
    },
    {
      label: 'Configurações',
      group: 'Sistema',
      description: 'Sistema e organização',
      path: '/settings',
      icon: Settings2,
      permission: 'manager',
    },
  ]

  const filteredNavItems = navItems.filter((item) => {
    if (item.readerOnly) return !isManager
    if (item.permission === 'manager') return isManager
    if (item.permission === 'analytics') return canViewAnalytics()
    if (item.permission === 'admin') return isAdmin
    return true
  })

  return (
    <>
      {/* Overlay para mobile */}
      {isOpen && (
        <button
          type="button"
          className="fixed inset-0 bg-background/80 backdrop-blur-sm z-40 lg:hidden"
          onClick={onClose}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              onClose()
            }
          }}
          aria-label="Fechar menu lateral"
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed left-0 top-0 z-50 flex h-dvh w-64 flex-col border-r border-border/70 bg-background/90 shadow-2xl backdrop-blur-xl transition-transform duration-300 lg:translate-x-0',
          isOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex h-16 shrink-0 items-center justify-between border-b border-border/70 px-4">
          <Link to="/dashboard" className="flex items-center gap-2">
            {branding.branding.logoUrl ? (
              <img
                src={branding.branding.logoUrl}
                alt={`Logo ${branding.branding.displayName}`}
                className="h-10 w-10 rounded-2xl object-cover shadow-lg shadow-primary/20"
              />
            ) : (
              <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[linear-gradient(135deg,hsl(var(--primary)),hsl(var(--primary)/0.72))] shadow-lg shadow-primary/20">
                <span className="text-lg font-bold text-primary-foreground">
                  {branding.branding.displayName.slice(0, 1).toUpperCase()}
                </span>
              </div>
            )}
            <div>
              <span className="block text-lg font-semibold">{branding.branding.displayName}</span>
              <span className="text-xs uppercase tracking-wide leading-tight text-muted-foreground">
                comunicação interna
              </span>
            </div>
          </Link>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="lg:hidden"
            aria-label="Fechar menu"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        <nav aria-label="Navegação principal" className="min-h-0 flex-1 overflow-y-auto p-4">
          <div className="space-y-5">
            {['Visão geral', 'Comunicação', 'Acompanhamento', 'Sistema'].map((group) => {
              const items = filteredNavItems.filter((item) => item.group === group)

              if (items.length === 0) return null

              return (
                <section key={group} aria-label={group}>
                  <h2 className="mb-2 px-3 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                    {group}
                  </h2>
                  <div className="flex flex-col gap-1">
                    {items.map((item) => {
                      const Icon = item.icon
                      const isActive =
                        location.pathname === item.path ||
                        location.pathname.startsWith(`${item.path}/`) ||
                        (item.path === '/settings' && isSettingsPath(location.pathname))

                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          aria-current={isActive ? 'page' : undefined}
                          onClick={onClose}
                          className={cn(
                            'group relative flex items-center gap-3 rounded-2xl border px-3 py-2.5 text-sm font-medium transition-all',
                            isActive
                              ? 'border-primary/35 bg-primary/12 text-foreground shadow-md shadow-primary/10 ring-1 ring-primary/15'
                              : 'border-transparent text-muted-foreground hover:border-border/80 hover:bg-card hover:text-foreground hover:shadow-sm',
                          )}
                        >
                          <span
                            className={cn(
                              'absolute left-1.5 top-1/2 h-8 w-1 -translate-y-1/2 rounded-full transition-all',
                              isActive
                                ? 'bg-primary shadow-sm shadow-primary/40'
                                : 'bg-transparent',
                            )}
                          />
                          <div
                            className={cn(
                              'flex h-9 w-9 items-center justify-center rounded-xl transition-colors',
                              isActive
                                ? 'bg-primary text-primary-foreground shadow-sm'
                                : 'bg-muted/70 text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary',
                            )}
                          >
                            <Icon className="h-4 w-4" />
                          </div>
                          <div className="min-w-0">
                            <p
                              className={cn(
                                'leading-tight',
                                isActive && 'font-semibold text-foreground',
                              )}
                            >
                              {item.label}
                            </p>
                            <p
                              className={cn(
                                'text-xs leading-relaxed',
                                isActive ? 'text-primary/80' : 'text-muted-foreground/70',
                              )}
                            >
                              {item.description}
                            </p>
                          </div>
                        </Link>
                      )
                    })}
                  </div>
                </section>
              )
            })}
          </div>
        </nav>

        <div className="shrink-0 border-t border-border/70 p-3">
          <details className="group rounded-2xl bg-muted/60 px-3 py-2 text-xs text-muted-foreground">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-2 rounded-lg py-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
              <span>
                Versão <span className="font-medium text-foreground">{buildInfo.version}</span>
              </span>
              <ChevronDown
                aria-hidden="true"
                className="size-3.5 transition-transform group-open:rotate-180"
              />
            </summary>
            <div className="mt-2 space-y-1 border-t border-border/60 pt-2 text-xs">
              <p>Build em {formatBuildTimestamp(buildInfo.builtAtIso)}</p>
              <p className="truncate">Branch {buildInfo.gitBranch}</p>
            </div>
          </details>
        </div>
      </aside>
    </>
  )
}
